import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Save, Plus, X, Video } from 'lucide-react';
import { toast } from 'sonner';
import MediaUploadSection from './MediaUploadSection';
import SongsSection from './SongsSection';
import { UnifiedScheduleSection } from '@/components/displays/schedule';
import { DisplaySchedule } from '@/types/display';
import { HolidayValue } from '@/constants/holidays';
import { useMediaUpload } from './hooks/useMediaUpload';
import {
  useHolidayYear,
  useHolidaySongs,
  useUpsertHolidayYear,
  useSaveHolidayContent,
  useSaveSongs
} from '@/hooks/useHolidayData';

interface DisplaySong {
  id?: string;
  title: string;
  artist: string;
  duration?: string;
  year_introduced: number;
  youtube_url?: string;
  display_year_id?: string;
  display_holiday_year_id?: string;
  album_cover_url?: string;
}

interface HolidayContentEditorProps {
  displayId: number;
  holiday: HolidayValue;
  year?: number;
  city?: string;
  state?: string;
  onContentChange?: () => void;
}

/**
 * HolidayContentEditor - Manages all content specific to one holiday
 *
 * This component handles editing:
 * - Description (holiday-specific)
 * - Photos (holiday-specific)
 * - Videos (sequence recordings for this holiday)
 * - Songs (songs used for this holiday)
 * - Schedule (when this holiday display is active)
 */
export const HolidayContentEditor: React.FC<HolidayContentEditorProps> = ({
  displayId,
  holiday,
  year = new Date().getFullYear(),
  city,
  state,
  onContentChange
}) => {
  const { uploadingImages, uploadImages } = useMediaUpload();

  // React Query hooks for data fetching and caching
  const { data: holidayYear, isLoading: isLoadingYear } = useHolidayYear(displayId, holiday, year);
  const { data: songsData = [], isLoading: isLoadingSongs } = useHolidaySongs(holidayYear?.id || null);
  const upsertMutation = useUpsertHolidayYear();
  const saveContentMutation = useSaveHolidayContent();
  const saveSongsMutation = useSaveSongs();

  // Local state for editing (synced with server data)
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState<string[]>([]);
  const [videos, setVideos] = useState<string[]>([]);
  const [schedule, setSchedule] = useState<DisplaySchedule | null>(null);
  const [songs, setSongs] = useState<DisplaySong[]>([{
    title: '',
    artist: '',
    year_introduced: year,
    youtube_url: ''
  }]);

  // Video input state
  const [newVideoUrl, setNewVideoUrl] = useState('');

  // Sync local state with fetched data
  useEffect(() => {
    if (holidayYear) {
      setDescription(holidayYear.description || '');
      setPhotos((holidayYear.photos as string[]) || []);
      setVideos((holidayYear.videos as string[]) || []);
      setSchedule((holidayYear.schedule as DisplaySchedule) || null);
    }
  }, [holidayYear]);

  // Sync songs with fetched data
  useEffect(() => {
    if (songsData.length > 0) {
      setSongs(songsData.map(song => ({
        id: song.id,
        title: song.title,
        artist: song.artist,
        duration: song.duration || undefined,
        year_introduced: song.year_introduced,
        youtube_url: song.video_url || undefined,
        album_cover_url: song.album_cover_url || undefined,
        display_holiday_year_id: song.display_holiday_year_id
      })));
    }
  }, [songsData]);

  // Auto-create holiday year record if it doesn't exist
  useEffect(() => {
    if (!isLoadingYear && !holidayYear) {
      upsertMutation.mutate({
        displayId,
        holiday,
        year
      });
    }
  }, [isLoadingYear, holidayYear, displayId, holiday, year]);

  const handleSaveContent = async () => {
    if (!holidayYear?.id) {
      toast.error('Holiday year record not initialized');
      return;
    }

    try {
      await saveContentMutation.mutateAsync({
        holidayYearId: holidayYear.id,
        content: {
          description,
          photos,
          videos,
          schedule: schedule || undefined
        }
      });

      toast.success(`${holiday} content saved successfully`);
      onContentChange?.();
    } catch (error) {
      console.error('Error saving holiday content:', error);
      toast.error('Failed to save content');
    }
  };

  const handleSaveSongs = async () => {
    if (!holidayYear?.id) {
      toast.error('Holiday year record not initialized');
      return;
    }

    try {
      await saveSongsMutation.mutateAsync({
        holidayYearId: holidayYear.id,
        songs: songs.map(song => ({
          id: song.id,
          title: song.title,
          artist: song.artist,
          duration: song.duration,
          year_introduced: song.year_introduced,
          video_url: song.youtube_url,
          album_cover_url: song.album_cover_url
        }))
      });

      toast.success(`${holiday} songs saved successfully`);
      onContentChange?.();
    } catch (error) {
      console.error('Error saving songs:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save songs');
    }
  };

  const handleUploadImages = async (files: FileList) => {
    try {
      const uploadedUrls = await uploadImages(files);
      const newPhotos = [...photos, ...uploadedUrls];
      setPhotos(newPhotos);
    } catch (error) {
      console.error('Failed to upload images:', error);
      toast.error('Failed to upload images');
    }
  };

  const handlePhotosChange = (newPhotos: string[]) => {
    setPhotos(newPhotos);
  };

  const handleScheduleChange = (newSchedule: DisplaySchedule) => {
    setSchedule(newSchedule);
  };

  const handleAddVideo = () => {
    if (!newVideoUrl.trim()) return;

    setVideos([...videos, newVideoUrl.trim()]);
    setNewVideoUrl('');
  };

  const handleRemoveVideo = (index: number) => {
    setVideos(videos.filter((_, i) => i !== index));
  };

  const isLoading = isLoadingYear || isLoadingSongs || upsertMutation.isPending;
  const isSaving = saveContentMutation.isPending || saveSongsMutation.isPending;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-2 border-current border-t-transparent rounded-full mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Loading {holiday} content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Description */}
      <Card>
        <CardHeader>
          <CardTitle>Description</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={`Describe your ${holiday} display...`}
            rows={4}
          />
          <p className="text-xs text-muted-foreground mt-2">
            Tell visitors what makes your {holiday} display special
          </p>
        </CardContent>
      </Card>

      {/* Photos */}
      <MediaUploadSection
        images={photos}
        uploadingImages={uploadingImages}
        maxImages={10}
        onImagesChange={handlePhotosChange}
        onUploadImages={handleUploadImages}
      />

      {/* Videos */}
      <Card>
        <CardHeader>
          <CardTitle>
            <Video className="inline-block w-4 h-4 mr-2" />
            Sequence Videos
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Add Video URL (YouTube, etc.)</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={newVideoUrl}
                onChange={(e) => setNewVideoUrl(e.target.value)}
                placeholder="https://youtube.com/watch?v=..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddVideo();
                  }
                }}
              />
              <Button
                type="button"
                onClick={handleAddVideo}
                disabled={!newVideoUrl.trim()}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {videos.length > 0 && (
            <div className="space-y-2">
              <Label>Videos ({videos.length})</Label>
              {videos.map((video, index) => (
                <div key={index} className="flex items-center gap-2 p-2 bg-muted rounded">
                  <Video className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="text-sm truncate flex-1">{video}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveVideo(index)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Schedule */}
      <UnifiedScheduleSection
        schedule={schedule || undefined}
        city={city}
        state={state}
        onChange={handleScheduleChange}
        defaultTab="basic"
        showAdvanced={true}
      />

      {/* Songs */}
      <SongsSection
        songs={songs}
        maxSongs={100}
        onSongsChange={setSongs}
      />

      {/* Save Buttons */}
      <div className="flex justify-end space-x-2 sticky bottom-0 bg-background py-4 border-t">
        <Button
          type="button"
          variant="outline"
          onClick={handleSaveSongs}
          disabled={isSaving}
        >
          <Save className="w-4 h-4 mr-2" />
          Save Songs
        </Button>
        <Button
          type="button"
          onClick={handleSaveContent}
          disabled={isSaving}
        >
          <Save className="w-4 h-4 mr-2" />
          Save {holiday} Content
        </Button>
      </div>
    </div>
  );
};

export default HolidayContentEditor;
